import { getApiBaseUrl } from '../netWork/WebConfig';

/**
 * 后端发送该事件时，前端会重新请求订单列表。
 *
 * 支持的 SSE 消息格式：
 * event: ORDER_LIST_REFRESH
 * data: {"type":"ORDER_LIST_REFRESH"}
 */
export const ORDER_LIST_REFRESH_EVENT = 'ORDER_LIST_REFRESH';

/**
 * SSE 接口相对于 API 基础地址的路径。
 * 最终地址示例：/api/v1/booking/security/order/sse
 */
export const SECURITY_ORDER_SSE_PATH = '/booking/security/order/sse';

/** SSE 标准消息中前端实际需要使用的字段。 */
export interface SecurityOrderSseEvent {
    event: string;
    data: string;
    id?: string;
}

/** SSE 服务配置。 */
interface SecurityOrderSseOptions {
    /** 收到 ORDER_LIST_REFRESH 后执行的刷新函数。 */
    onRefresh: () => void | Promise<void>;
    /** 可选的自定义 SSE 地址；不传时使用 SECURITY_ORDER_SSE_PATH。 */
    endpoint?: string;
    /** 连接失败或断开时的回调。 */
    onError?: (error: unknown) => void;
    /** 自定义消息判断逻辑；不传时使用 defaultShouldRefresh。 */
    shouldRefresh?: (event: SecurityOrderSseEvent) => boolean;
    /** 首次断线重连等待时间，默认 2 秒。 */
    reconnectDelayMs?: number;
    /** 最大断线重连等待时间，默认 30 秒。 */
    maxReconnectDelayMs?: number;
}

/** 拼接 API 基础地址和 SSE 路径，同时兼容绝对地址。 */
const joinUrl = (baseUrl: string, path: string) => {
    if (/^https?:\/\//i.test(path)) return path;
    return `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
};

/**
 * 默认的刷新条件。
 * 只接受约定的 ORDER_LIST_REFRESH，不会因为心跳或其他普通消息刷新列表。
 */
const defaultShouldRefresh = (event: SecurityOrderSseEvent) => {
    if (event.event === ORDER_LIST_REFRESH_EVENT) return true;

    const data = event.data.trim();
    if (data === ORDER_LIST_REFRESH_EVENT) return true;

    try {
        const payload = JSON.parse(data) as Record<string, unknown>;
        return payload.type === ORDER_LIST_REFRESH_EVENT
            || payload.event === ORDER_LIST_REFRESH_EVENT
            || payload.refresh === ORDER_LIST_REFRESH_EVENT;
    } catch {
        return false;
    }
};

/**
 * 订单 SSE 客户端。
 *
 * 使用 fetch 读取 text/event-stream，而不是原生 EventSource，
 * 这样可以携带项目现有的 Bearer Token 请求头。
 */
class SecurityOrderSseService {
    private readonly options: SecurityOrderSseOptions;
    private readonly endpoint: string;
    private readonly initialReconnectDelay: number;
    private readonly maxReconnectDelay: number;
    private reconnectDelay: number;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private abortController: AbortController | null = null;
    private running = false;

    constructor(options: SecurityOrderSseOptions) {
        this.options = options;
        this.endpoint = options.endpoint
            ? joinUrl(getApiBaseUrl(), options.endpoint)
            : joinUrl(getApiBaseUrl(), SECURITY_ORDER_SSE_PATH);
        this.initialReconnectDelay = options.reconnectDelayMs ?? 2000;
        this.maxReconnectDelay = options.maxReconnectDelayMs ?? 30000;
        this.reconnectDelay = this.initialReconnectDelay;
    }

    /** 开始建立 SSE 连接；重复调用不会创建重复连接。 */
    public start() {
        if (this.running) return;
        this.running = true;
        void this.connect();
    }

    /** 停止读取、取消当前请求，并清理待执行的重连定时器。 */
    public stop() {
        this.running = false;
        this.abortController?.abort();
        this.abortController = null;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /** 建立一次连接；连接结束后由 scheduleReconnect 负责重连。 */
    private async connect() {
        if (!this.running) return;

        this.abortController = new AbortController();
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(this.endpoint, {
                method: 'GET',
                cache: 'no-store',
                signal: this.abortController.signal,
                headers: {
                    Accept: 'text/event-stream',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });

            if (!response.ok) throw new Error(`SSE connection failed: ${response.status}`);
            if (!response.body) throw new Error('SSE response does not provide a readable stream');

            this.reconnectDelay = this.initialReconnectDelay;
            await this.consume(response.body);
        } catch (error) {
            if (this.running && !(error instanceof DOMException && error.name === 'AbortError')) {
                this.options.onError?.(error);
            }
        } finally {
            this.abortController = null;
            this.scheduleReconnect();
        }
    }

    /** 使用指数退避，避免服务不可用时频繁请求后端。 */
    private scheduleReconnect() {
        if (!this.running || this.reconnectTimer) return;

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            void this.connect();
        }, this.reconnectDelay);
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
    }

    /**
     * 按 SSE 协议解析流数据：
     * event: 事件名称
     * data: 事件内容
     * id: 事件 ID
     * 空行表示一条消息结束。
     */
    private async consume(stream: ReadableStream<Uint8Array>) {
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let eventName = 'message';
        let eventId: string | undefined;
        let dataLines: string[] = [];

        const dispatch = () => {
            if (dataLines.length === 0) return;

            const event: SecurityOrderSseEvent = {
                event: eventName,
                data: dataLines.join('\n'),
                id: eventId,
            };
            const shouldRefresh = this.options.shouldRefresh ?? defaultShouldRefresh;
            if (shouldRefresh(event)) void this.options.onRefresh();

            eventName = 'message';
            eventId = undefined;
            dataLines = [];
        };

        while (this.running) {
            const { value, done } = await reader.read();
            buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });
            const lines = buffer.split(/\r?\n/);
            buffer = lines.pop() ?? '';

            for (const line of lines) {
                if (line === '') {
                    dispatch();
                    continue;
                }
                if (line.startsWith(':')) continue;

                const separatorIndex = line.indexOf(':');
                const field = separatorIndex >= 0 ? line.slice(0, separatorIndex) : line;
                const valueText = separatorIndex >= 0
                    ? line.slice(separatorIndex + 1).replace(/^ /, '')
                    : '';

                if (field === 'event') eventName = valueText;
                if (field === 'data') dataLines.push(valueText);
                if (field === 'id') eventId = valueText;
            }

            if (done) break;
        }

        if (buffer) dataLines.push(buffer.replace(/^data:\s?/, ''));
        dispatch();
        reader.releaseLock();
    }
}

export default SecurityOrderSseService;
